import React, { useState } from "react";
import { Helmet } from "react-helmet";
import ERPModal from "../Components/ERPModal.js";
import { COLORS } from "../constants/colors";

import {
  FaUser,
  FaCalendarCheck,
  FaCalendarAlt,
  FaPaperclip,
  FaBook,
  FaTasks,
  FaHome,
  FaUserCheck,
  FaChartLine,
  FaChartBar,
  FaFileAlt,
  FaDollarSign,
  FaBookOpen,
  FaFileInvoice,
  FaBell,
  FaTrophy,
  FaEnvelopeOpenText,
} from "react-icons/fa";

const TeacherPanel = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const features = [
    {
      title: "Profile",
      description:
        "View and update your personal information.",
      icon: <FaUser />,
    },

    {
      title: "Attendance",
      description: "Track your daily attendance.",
      icon: <FaCalendarCheck />,
    },

    {
      title: "Schedule",
      description: "View your class schedule.",
      icon: <FaCalendarAlt />,
    },

    {
      title: "Test Schedule",
      description:
        "Manage test schedules for your classes.",
      icon: <FaPaperclip />,
    },

    {
      title: "Course Material",
      description:
        "Access and upload course materials.",
      icon: <FaBook />,
    },

    {
      title: "Assignments",
      description:
        "Create and monitor assignments for students.",
      icon: <FaTasks />,
    },

    {
      title: "Homework",
      description:
        "Assign and track homework for students.",
      icon: <FaHome />,
    },

    {
      title: "Student Attendance",
      description:
        "Manage attendance for your classes.",
      icon: <FaUserCheck />,
    },

    {
      title: "Result (Subject Wise)",
      description:
        "Manage student results by subject.",
      icon: <FaChartLine />,
    },

    {
      title: "Result (Class In-Charge)",
      description:
        "Manage results for your class.",
      icon: <FaChartBar />,
    },

    {
      title: "Paper",
      description:
        "Create and manage test papers.",
      icon: <FaFileAlt />,
    },

    {
      title: "View Salary",
      description:
        "Check your salary details.",
      icon: <FaDollarSign />,
    },

    {
      title: "Ledger",
      description:
        "View your financial ledger.",
      icon: <FaBookOpen />,
    },

    {
      title: "Pending Fee Report",
      description:
        "Access reports on pending fees.",
      icon: <FaFileInvoice />,
    },

    {
      title: "Notifications",
      description:
        "Send and view notifications.",
      icon: <FaBell />,
    },

    {
      title: "Student Achievements",
      description:
        "Highlight and view student achievements.",
      icon: <FaTrophy />,
    },
  ];

  return (
    <>
      {/* SEO HEAD */}
      <Helmet>
        {/* Primary SEO */}
        <title>
          Teacher Panel Software for Schools | Gurukul ERP
        </title>

        <meta
          name="description"
          content="Gurukul ERP Teacher Panel helps teachers manage attendance, assignments, homework, schedules, results, class activities, notifications, and student performance from one powerful dashboard."
        />

        <meta
          name="keywords"
          content="
          teacher panel software,
          school teacher management system,
          teacher dashboard software,
          teacher ERP panel,
          school ERP teacher panel,
          teacher attendance software,
          online teacher management system,
          school management software for teachers,
          assignment management system,
          homework management software,
          teacher portal software,
          class management software,
          student attendance system,
          result management software,
          teacher scheduling software,
          Gurukul ERP,
          teacher dashboard India,
          cloud based school ERP,
          education ERP software,
          teacher management platform
          "
        />

        <meta
          name="author"
          content="Gurukul ERP"
        />

        <meta
          name="robots"
          content="index, follow"
        />

        <meta
          name="language"
          content="English"
        />

        <meta
          name="revisit-after"
          content="2 days"
        />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://gurukulerp.in/teacher-panel"
        />

        {/* Open Graph SEO */}
        <meta
          property="og:title"
          content="Teacher Panel Software for Schools | Gurukul ERP"
        />

        <meta
          property="og:description"
          content="Modern teacher panel software for attendance, assignments, schedules, homework, results, notifications, and classroom management."
        />

        <meta
          property="og:image"
          content="https://gurukulerp.in/assets/Teacher_dashboard.jpeg"
        />

        <meta
          property="og:url"
          content="https://gurukulerp.in/teacher-panel"
        />

        <meta
          property="og:type"
          content="website"
        />

        <meta
          property="og:site_name"
          content="Gurukul ERP"
        />

        {/* Twitter SEO */}
        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="Teacher Panel Software for Schools | Gurukul ERP"
        />

        <meta
          name="twitter:description"
          content="Powerful teacher dashboard software for attendance, assignments, results, schedules, student management, and classroom activities."
        />

        <meta
          name="twitter:image"
          content="https://gurukulerp.in/assets/Teacher_dashboard.jpeg"
        />

        {/* Mobile SEO */}
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0"
        />

        <meta
          name="theme-color"
          content="#3A4163"
        />

        {/* Geo SEO */}
        <meta
          name="geo.region"
          content="IN-HR"
        />

        <meta
          name="geo.placename"
          content="Panipat, Haryana"
        />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context":
              "https://schema.org",
            "@type":
              "SoftwareApplication",
            name:
              "Gurukul ERP Teacher Panel",
            applicationCategory:
              "BusinessApplication",
            operatingSystem: "Web",
            url:
              "https://gurukulerp.in/teacher-panel",
            image:
              "https://gurukulerp.in/assets/Teacher_dashboard.jpeg",

            description:
              "Teacher panel software for attendance, assignments, homework, schedules, student performance, notifications, and school management.",

            brand: {
              "@type": "Brand",
              name: "Gurukul ERP",
            },

            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "INR",
            },
          })}
        </script>
      </Helmet>

      {/* MAIN CONTAINER */}
      <div
        className="min-h-screen"
        style={{
          background: `linear-gradient(to bottom, ${COLORS.white}, ${COLORS.medium}, ${COLORS.light})`,
          color: COLORS.textPrimary,
        }}
      >
        {/* HERO SECTION */}
        <header className="relative overflow-hidden py-24 text-center">
          <div className="absolute inset-0 bg-gradient-to-b from-[#CBE3EF]/70 via-[#EDF3FB]/60 to-[#FFFFFF]"></div>

          <div className="relative z-10 max-w-5xl mx-auto px-6">
            <h1 className="text-5xl md:text-6xl font-extrabold mb-6 leading-tight">
              Empower Teachers with{" "}
              <span
                style={{
                  color: COLORS.primary,
                }}
              >
                Gurukul ERP
              </span>
            </h1>

            <p className="text-lg md:text-xl text-[#4B5563] max-w-3xl mx-auto mb-10 leading-relaxed">
              Manage your teaching tasks effectively
              with attendance tracking, assignments,
              schedules, homework, results, and
              classroom management tools.
            </p>

            <div className="flex justify-center gap-6 flex-wrap">
              <button
                onClick={() =>
                  setIsModalOpen(true)
                }
                style={{
                  backgroundColor:
                    COLORS.primary,
                }}
                className="text-white px-10 py-4 rounded-xl font-semibold shadow-md hover:bg-[#3A4163] hover:scale-105 transition-transform duration-300"
              >
                Try Teacher Panel Demo
              </button>
            </div>
          </div>

          <div className="absolute w-72 h-72 bg-[#5AA8D6]/20 rounded-full blur-3xl top-20 left-16"></div>

          <div className="absolute w-80 h-80 bg-[#3A4163]/10 rounded-full blur-3xl bottom-10 right-20"></div>
        </header>

        {/* ABOUT SECTION */}
        <section className="flex flex-col lg:flex-row items-center py-16 px-8">
          <div className="lg:w-1/2 flex justify-center mb-8 lg:mb-0">
            <img
              src="/assets/Teacher_dashboard.jpeg"
              alt="Teacher Panel Dashboard Software"
              className="rounded-2xl shadow-2xl w-4/5 transform hover:scale-105 transition-transform"
            />
          </div>

          <div className="lg:w-1/2 lg:pl-12 text-center lg:text-left">
            <h2
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                color:
                  COLORS.textPrimary,
              }}
            >
              About Gurukul Teacher Panel
            </h2>

            <p
              className="text-lg leading-relaxed"
              style={{
                color:
                  COLORS.textSecondary,
              }}
            >
              The Gurukul ERP Teacher Panel
              enhances the teaching experience
              with intuitive tools for class
              management, attendance tracking,
              scheduling, assignments, homework,
              student results, and performance
              monitoring.
            </p>
          </div>
        </section>

        {/* FEATURES */}
        <section
          className="py-20"
          style={{
            background: `linear-gradient(to bottom, ${COLORS.background}, ${COLORS.primaryLight})`,
          }}
        >
          <div className="container mx-auto px-6">
            <h2
              className="text-4xl font-bold text-center mb-14"
              style={{
                color:
                  COLORS.textPrimary,
              }}
            >
              Key Features of Teacher Panel
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
              {features.map(
                (feature, index) => (
                  <div
                    key={index}
                    className="group relative [perspective:1200px]"
                  >
                    <div className="relative h-40 w-full transition-all duration-700 ease-out [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]">
                      {/* FRONT */}
                      <div
                        className="absolute inset-0 rounded-2xl shadow-lg flex flex-col items-center justify-center text-white text-center p-2 [backface-visibility:hidden] transition-all duration-500 group-hover:shadow-2xl group-hover:scale-105"
                        style={{
                          background: `linear-gradient(145deg, ${COLORS.primary}, ${COLORS.primaryDark})`,
                        }}
                      >
                        <div className="text-2xl mb-3 drop-shadow-lg">
                          {feature.icon}
                        </div>

                        <h3 className="text-md font-bold">
                          {feature.title}
                        </h3>
                      </div>

                      {/* BACK */}
                      <div
                        className="absolute inset-0 rounded-2xl bg-white shadow-xl flex flex-col items-center justify-center text-center p-5 [transform:rotateY(180deg)] [backface-visibility:hidden] transition-all duration-700"
                        style={{
                          color:
                            COLORS.textPrimary,
                        }}
                      >
                        <p className="text-md leading-relaxed font-medium">
                          {
                            feature.description
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </section>

        {/* REQUEST DEMO */}
        <section className="mt-15 pt-10 pb-10 text-center bg-gradient-to-b from-[#CBE3EF]/20 via-[#EDF3FB]/60 to-[#FFFFFF]">
          <div className="max-w-3xl mx-auto bg-white p-10 rounded-2xl shadow-2xl">
            <div className="flex justify-center mb-6">
              <FaEnvelopeOpenText
                className="text-5xl"
                style={{
                  color:
                    COLORS.primary,
                }}
              />
            </div>

            <h2
              className="text-3xl font-bold mb-4"
              style={{
                color:
                  COLORS.textPrimary,
              }}
            >
              Request Demo Credentials
            </h2>

            <p
              className="mb-8 text-lg"
              style={{
                color:
                  COLORS.textSecondary,
              }}
            >
              Want to explore Gurukul Teacher
              Panel? Request demo credentials
              below and see how easy teaching
              management becomes.
            </p>

            <div className="flex flex-col md:flex-row justify-center gap-6">
              <button
                onClick={() =>
                  setIsModalOpen(true)
                }
                className="text-white px-10 py-4 rounded-lg font-semibold shadow-md transition-all"
                style={{
                  backgroundColor:
                    COLORS.primary,
                }}
              >
                Request Demo
              </button>

              <a
                href="#"
                className="px-10 py-4 rounded-lg font-semibold shadow-md transition-all"
                style={{
                  backgroundColor:
                    COLORS.medium,
                  color:
                    COLORS.textPrimary,
                }}
              >
                Try Demo
              </a>
            </div>
          </div>
        </section>

        {/* SEO CONTENT */}
        <section className="py-20 px-6 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">
              Why Teachers Prefer Gurukul ERP
              Teacher Panel
            </h2>

            <p className="text-lg leading-9 text-gray-700 mb-6">
              Gurukul ERP Teacher Panel helps
              educators manage daily classroom
              activities efficiently through a
              secure and easy-to-use dashboard.
              Teachers can manage attendance,
              assignments, homework, schedules,
              student results, notifications,
              and academic activities from one
              place.
            </p>

            <p className="text-lg leading-9 text-gray-700 mb-6">
              The platform improves classroom
              productivity by reducing manual
              work and simplifying communication
              between teachers, students, and
              administrators. Schools can use
              the teacher dashboard to track
              academic performance, manage
              papers, and improve operational
              efficiency.
            </p>

            <p className="text-lg leading-9 text-gray-700">
              Gurukul ERP is a modern cloud
              based school ERP software designed
              for schools, coaching institutes,
              and educational organizations
              looking to digitize teacher
              management and classroom
              operations.
            </p>
          </div>
        </section>

        {/* MODAL */}
        <ERPModal
          isOpen={isModalOpen}
          onClose={() =>
            setIsModalOpen(false)
          }
        />
      </div>
    </>
  );
};

export default TeacherPanel;